import { Tooltip } from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { ErrorBoundary } from '@sentry/react';
import React, { PropsWithChildren, Suspense } from 'react';
import { graphql, useSafeRelay } from 'utils/graphClient';
import { useAuthenticatedSession } from 'utils/sessionProvider';
import { ReactionTooltip_members_Query } from './__generated__/ReactionTooltip_members_Query.graphql';

const membersQuery = graphql`
  query ReactionTooltip_members_Query($ids: [ID!]!) {
    members: nodes(ids: $ids) {
      ... on Member {
        id
        name
        email
        ...UserAvatar_member
        ...UserHandle_member
      }
    }
  }
`;

interface ReactionTooltipProps {
  memberIds: string[];
  emoji: string;
}

const UnsuspendedReactionTooltip: React.FC<
  PropsWithChildren<ReactionTooltipProps>
> = ({ memberIds, emoji, children }) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const { members } = useLazyLoadQuery<ReactionTooltip_members_Query>(
    membersQuery,
    {
      ids: memberIds,
    },
  );
  const { member: sessionMember } = useAuthenticatedSession();

  const tooltipText = `${members
    .map((m) => {
      if (m?.id === sessionMember.id) {
        return t`you`;
      }
      return m?.name;
    })
    .join(', ')} reacted with ${emoji}`;

  const tooltipTextCapitalized =
    tooltipText.charAt(0).toUpperCase() + tooltipText.slice(1);
  return (
    <ReactionTooltipSkeleton label={tooltipTextCapitalized}>
      {children}
    </ReactionTooltipSkeleton>
  );
};

export const ReactionTooltip: React.FC<
  PropsWithChildren<ReactionTooltipProps>
> = ({ ...props }) => {
  return (
    <ErrorBoundary
      fallback={
        <ReactionTooltipSkeleton
          label={t`Could not load reactions.`}
          {...props}
        />
      }
    >
      <Suspense
        fallback={
          <ReactionTooltipSkeleton label={t`Loading reactions...`} {...props} />
        }
      >
        <UnsuspendedReactionTooltip {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

const ReactionTooltipSkeleton = ({
  label,
  children,
}: PropsWithChildren<{ label: string }>) => (
  <Tooltip hasArrow openDelay={500} placement="top" label={label}>
    {children}
  </Tooltip>
);
